import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { formatMoney } from '@motoschool/shared';
import type { Currency } from '@motoschool/shared';
import { api, ApiRequestError } from '../lib/api';
import { Badge, Button, ErrorNotice, Field, Input, Sheet } from './ui';
import type { PickedLesson } from './PublicTimePicker';
import { lessonDurationLabel } from '../lib/useTeachers';
import { formatTime, humanDayLabel } from '../lib/format';

interface GuestBookingSheetProps {
  lesson: PickedLesson | null;
  /** Куда отправлять заявку: общий роут или роут конкретной ссылки. */
  endpoint: string;
  timeZone: string;
  currency: Currency;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Форма записи для человека без аккаунта. Полей ровно два: имя и телефон —
 * этого достаточно, чтобы инструктор перезвонил и подтвердил запись.
 * Всё остальное выясняется в разговоре и только мешало бы отправить заявку.
 */
export function GuestBookingSheet({
  lesson,
  endpoint,
  timeZone,
  currency,
  onClose,
  onSuccess,
}: GuestBookingSheetProps): React.JSX.Element {
  const [form, setForm] = useState({ name: '', phone: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = useMutation({
    mutationFn: () =>
      api.post(
        endpoint,
        {
          teacherId: lesson?.teacherId,
          startsAt: lesson?.startsAt,
          durationMinutes: lesson?.durationMinutes,
          guestName: form.name,
          guestPhone: form.phone,
        },
        { anonymous: true },
      ),
    onSuccess: () => {
      setForm({ name: '', phone: '' });
      setError(null);
      setFieldErrors({});
      onSuccess();
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFieldErrors(caught.fieldErrors);
      } else {
        setError('Не удалось отправить заявку, попробуйте ещё раз');
      }
    },
  });

  const endsAt =
    lesson === null
      ? null
      : new Date(new Date(lesson.startsAt).getTime() + lesson.durationMinutes * 60_000);

  return (
    <Sheet
      isOpen={lesson !== null}
      title="Запись на занятие"
      onClose={onClose}
      footer={
        <div className="flex gap-2 pb-1">
          <Button variant="secondary" fullWidth onClick={onClose}>
            Отмена
          </Button>
          <Button fullWidth isLoading={submit.isPending} onClick={() => submit.mutate()}>
            Отправить заявку
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNotice message={error} />}

        {lesson && endsAt && (
          <div className="rounded-xl bg-canvas px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm">{humanDayLabel(lesson.startsAt, timeZone)}</span>
              <Badge tone="brand">
                {formatTime(lesson.startsAt, timeZone)} —{' '}
                {formatTime(endsAt.toISOString(), timeZone)}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted">
              {lesson.teacherName ? `${lesson.teacherName} · ` : ''}
              {lessonDurationLabel(lesson.durationMinutes)}
              {lesson.priceMinor === null
                ? ''
                : ` · ${formatMoney(lesson.priceMinor, currency)}`}
            </p>
          </div>
        )}

        <Field label="Как вас зовут" error={fieldErrors.guestName} required>
          <Input
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            autoComplete="name"
          />
        </Field>

        <Field label="Телефон" error={fieldErrors.guestPhone} required>
          <Input
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 701 000-00-00"
          />
        </Field>

        <p className="text-xs text-muted">
          Регистрация не нужна. Инструктор перезвонит, подтвердит запись и заведёт вам
          аккаунт — в нём будут видны занятия, баланс часов и оплаты.
        </p>
      </div>
    </Sheet>
  );
}
